/**
 * imgSrc — append a ?w=<width> query so the backend serves a WebP
 * thumbnail (cached for 1 year) instead of the multi-MB original.
 *
 * Safe on any URL:
 *   • /api/files/* URLs  → hits the resize endpoint
 *   • External URLs      → returned unchanged
 *   • Empty / null       → returned as-is (caller renders fallback)
 *
 * The resize endpoint transparently falls back to the full file for
 * non-raster types (PDFs, GIFs, SVGs), so this is safe to blanket-apply.
 */
export const imgSrc = (url, width, quality) => {
    if (!url) return url;
    // Only rewrite URLs that route to our own /api/files/* endpoint.
    // Third-party CDN URLs (Cloudinary, S3, direct hotlinks) don't
    // understand our resize params, so leave them untouched.
    if (!/\/api\/files\//.test(url)) return url;
    const sep = url.includes("?") ? "&" : "?";
    const w = Math.round(width || 0);
    if (!w) return url;
    const q = quality ? `&q=${Math.round(quality)}` : "";
    return `${url}${sep}w=${w}${q}`;
};

/**
 * imgSrcSet — build a density-based srcset so retina / high-DPI
 * screens pull a sharper 2× (and optionally 3×) version, while
 * standard displays keep the small 1× thumb.
 *
 * Usage:
 *   <img
 *      src={imgSrc(url, 320, 70)}
 *      srcSet={imgSrcSet(url, 320, 70)}
 *      alt="..."
 *   />
 *
 * Returns empty string for non-/api/files URLs so the browser stays
 * on the plain `src`.
 */
export const imgSrcSet = (url, baseWidth, quality) => {
    if (!url || !/\/api\/files\//.test(url)) return undefined;
    const w = Math.round(baseWidth || 0);
    if (!w) return undefined;
    // Cap at the backend max (4096) — imgSrc will silently no-op above.
    const w2 = Math.min(w * 2, 4096);
    const w3 = Math.min(w * 3, 4096);
    const parts = [`${imgSrc(url, w, quality)} 1x`];
    if (w2 > w) parts.push(`${imgSrc(url, w2, quality)} 2x`);
    if (w3 > w2) parts.push(`${imgSrc(url, w3, quality)} 3x`);
    return parts.join(", ");
};

export default imgSrc;
